import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { filter, groupBy } from 'lodash';
import { LINE_ITEM_CATEGORY_GENERAL, LINE_ITEM_CATEGORY_RESTRICTIONS, LINE_ITEM_CATEGORY_PAYMENT_METHODS, STATUS_PAID, STATUS_WAITING_FOR_PAYMENT, ENGLISH_LANG, COUNTRIES_EXTN, PREFERRED_COUNTRIES, STATUS_CODE_AVAILABLE, STATUS_CODE_OUT_OF_SERVICE, STATUS_CODE_OCCUPIED, STATUS_CODE_CHARGING, STATUS_CODE_BOOKED, STATUS_CODE_FINISHING, STATUS_CODE_PREPARING, STATUS_CODE_SUSPENDEDEVSE, STATUS_CODE_SUSPENDEDDEV, STATUS_CODE_RESERVED, STATUS_CODE_UNAVAILABLE, STATUS_CODE_FAULTED } from 'configs/constants';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import params from 'configs/params';
import { Input, FormGroup, Button, Label, Table } from 'reactstrap';
import { PRICE_TYPE_EV_CHARGING_BASIC, LANGUAGES, PSP_TYPE_STRIPE, PSP_TYPE_SWISH, PSP_TYPE_TEXT_CREDIT_CARD, PSP_TYPE_TEXT_SWISH } from 'configs/constants';
import { CheckoutForm } from 'components/core';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { getText } from 'helpers/app/getText';
import moment from 'moment';
import Collapsible from 'react-collapsible';
import { faCreditCard, faWallet } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const EVChargingQuickshop = (props) => {
  const { t, isQuickshopRentalSet, language, timeLeft, quickshop, onStartCharging, onPayAlreadyStartedPOSSession, onStopCharging, isPaymentInProgress, setIsPaymentCanceled, payPOSSession, pspClientSecret, stripeAccountID } = props;

  let opts = {};
  if (stripeAccountID?.length > 0) {
    opts = { stripeAccount: stripeAccountID };
  }
  const stripePromise = loadStripe(params.stripePublicKey, opts);

  const options = {
    // passing the client secret obtained in step 2
    clientSecret: pspClientSecret,
    // Fully customizable with appearance API.
    appearance: {
      /*...*/
    },
  };

  const [selectedTrigger, setSelectedTrigger] = useState('');
  const [selectedPSPType, setSelectedPSPType] = useState('');
  const [isPhoneValidationFailed, setIsPhoneValidationFailed] = useState(false);
  const [isNoPSPTypeSelected, setIsNoPSPTypeSelected] = useState(false);
  const [phone, setPhone] = useState('');
  const [payMobilePhone, setPayMobilePhone] = useState('');
  const [email, setEmail] = useState('');
  const [sendReceipt, setSendReceipt] = useState(false);

  const onPay = (selectedPSPType) => {
    if (selectedPSPType?.length === 0) {
      setIsNoPSPTypeSelected(true);
      return;
    }
    setIsNoPSPTypeSelected(false);
    if (selectedPSPType === PSP_TYPE_SWISH && payMobilePhone.length === 0) {
      setIsPhoneValidationFailed(true);
    }

    if (quickshop.paymentProviderAccounts && Object.keys(quickshop.paymentProviderAccounts).length > 0) {
      const paymentProvider = filter(Object.values(quickshop.paymentProviderAccounts), { pspType: selectedPSPType })[0];

      let params = {
        salesChannelID: quickshop._id,
        salesChannelType: 'quickshop',
        paymentProviderAccountID: paymentProvider?.paymentProviderAccountID,
        customerEmail: email,
        customerPhone: phone,
      };
      if (quickshop?.posSessionID?.length > 0) {
        params.posSessionID = quickshop.posSessionID;
      }
      if (selectedPSPType === PSP_TYPE_SWISH) {
        params = {
          ...params,
          payMobilePhone,
        };
      }

      payPOSSession(params);
    }
  };

  const onCancel = () => {
    localStorage.removeItem('secret');
    localStorage.removeItem('stripeAccountID');
    setIsPhoneValidationFailed(false);
    setSelectedPSPType('');
    setIsPaymentCanceled();
  };

  const handleSelectedTrigger = (val) => {
    if (selectedTrigger !== val) {
      setSelectedTrigger(val);
    } else {
      setSelectedTrigger('');
    }
  };

  let publicPriceInfo = quickshop.publicPriceInfo ?? '';

  if (publicPriceInfo.indexOf('{product.salesPrice}') >= 0 && quickshop.salesPrice?.toString()?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.salesPrice}', quickshop.salesPrice);
  }

  if (publicPriceInfo.indexOf('{product.currency}') >= 0 && quickshop.currency?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.currency}', quickshop.currency);
  }

  if (publicPriceInfo.indexOf('{product.unitCode}') >= 0 && quickshop.unitCode?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.unitCode}', quickshop.unitCode);
  }

  if (publicPriceInfo.indexOf('{product.feeChargingPerMinute}') >= 0 && quickshop.evChargingBasicFees?.feeChargingPerMinute?.toString()?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.feeChargingPerMinute}', quickshop.evChargingBasicFees?.feeChargingPerMinute);
  }

  if (publicPriceInfo.indexOf('{product.feeStart}') >= 0 && quickshop.evChargingBasicFees?.feeStart?.toString()?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.feeStart}', quickshop.evChargingBasicFees?.feeStart);
  }

  if (publicPriceInfo.indexOf('{product.feeParkingPerMinute}') >= 0 && quickshop.evChargingBasicFees?.feeParkingPerMinute?.toString()?.length > 0) {
    publicPriceInfo = publicPriceInfo.replace('{product.feeParkingPerMinute}', quickshop.evChargingBasicFees?.feeParkingPerMinute);
  }

  const groupedPublicProductInformation = groupBy(quickshop.publicProductInformation, 'lineItemCategory');

  const groupedKeys = quickshop.publicProductInformation?.length > 0 ? Object.keys(groupedPublicProductInformation) : [];

  return (
    <div className="container">
      <div className="row">
        <div className="col"></div>
        {quickshop.isEnabled && moment().isBetween(moment.utc(quickshop.validPeriod.validFromDate), moment.utc(quickshop.validPeriod.validToDate)) ? (
          <div className="col-md-8">
            <div className="my-3 p-3 bg-white rounded text-center">
              <div className="header-div text-center d-inline-block">
                <img src={quickshop.imageURL || ''} className="header-img mb-20" />
                <p className="quickshop-name-label">
                  <b>{quickshop.quickShopName}</b>
                </p>
                {!isPaymentInProgress && <p>{quickshop?.quickShopDescription?.length > 200 ? `${quickshop.quickShopDescription.substring(0, 200)}...` : quickshop.quickShopDescription}</p>}
              </div>
              <div className="img-container">
                {quickshop.statusCode === STATUS_CODE_AVAILABLE && <img src="img/status-icons/icon_available.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_OUT_OF_SERVICE && <img src="img/status-icons/icon_unavailable.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_OCCUPIED && <img src="img/status-icons/icon_occupied.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_CHARGING && <img src="img/status-icons/icon_occupied.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_BOOKED && <img src="img/status-icons/icon_booked.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_FINISHING && <img src="img/status-icons/icon_preparing.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_PREPARING && <img src="img/status-icons/icon_preparing.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_SUSPENDEDEVSE && <img src="img/status-icons/icon_preparing.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_SUSPENDEDDEV && <img src="img/status-icons/icon_preparing.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_RESERVED && <img src="img/status-icons/icon_booked.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_UNAVAILABLE && <img src="img/status-icons/icon_unavailable.svg" className="small-status-img" />}
                {quickshop.statusCode === STATUS_CODE_FAULTED && <img src="img/status-icons/icon_unavailable.svg" className="small-status-img" />}
              </div>
              {quickshop.statusCode && <p className="text-center status-text">{getText(`main:${quickshop.statusCode}`)}</p>}
              <br />
              <div className="price-section mt-10">
                <h5 className="text-center">
                  <b>{getText('main:PRICE')}:</b>
                  <br />
                  <p className="lbl-price">{publicPriceInfo}</p>
                </h5>
              </div>
              {quickshop.priceType === PRICE_TYPE_EV_CHARGING_BASIC && quickshop.evChargingBasicFees && (
                <Collapsible trigger={<span className="rule-idx">{getText('main:priceinfo.SHOW_DETAILED_PRICE_INFO')}</span>} open={selectedTrigger === 'tariffs'} handleTriggerClick={() => handleSelectedTrigger('tariffs')}>
                  <Table responsive className="bg-white template-tbl">
                    <thead>
                      <tr>
                        <td className="text-bold text-left">{getText('main:priceinfo.TARIFFS')}</td>
                        <td className="wd-100px text-bold text-right">
                          {getText('main:priceinfo.PRICE')}&nbsp;({quickshop.currency})
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {quickshop.evChargingBasicFees?.feeStart?.toString()?.length > 0 && quickshop.evChargingBasicFees?.feeStart > 0 && (
                        <tr>
                          <td className="text-left">{getText('main:priceinfo.START_FEE')}</td>
                          <td className="wd-100px text-right">{quickshop.evChargingBasicFees?.feeStart.toFixed(2)}</td>
                        </tr>
                      )}
                      {quickshop.evChargingBasicFees?.feeEnergyPerKwh?.toString()?.length > 0 && quickshop.evChargingBasicFees?.feeEnergyPerKwh > 0 && (
                        <tr>
                          <td className="text-left">{getText('main:priceinfo.ENERGY')}</td>
                          <td className="wd-100px text-right">{quickshop.evChargingBasicFees?.feeEnergyPerKwh.toFixed(2)}</td>
                        </tr>
                      )}
                      {quickshop.evChargingBasicFees?.feeParkingPerMinute?.toString()?.length > 0 && quickshop.evChargingBasicFees?.feeParkingPerMinute > 0 && (
                        <tr>
                          <td className="text-left">{getText('main:priceinfo.PARKING_TIME')}</td>
                          <td className="wd-100px text-right">{quickshop.evChargingBasicFees?.feeParkingPerMinute.toFixed(2)}</td>
                        </tr>
                      )}
                      {quickshop.evChargingBasicFees?.feeChargingPerMinute?.toString()?.length > 0 && quickshop.evChargingBasicFees?.feeChargingPerMinute > 0 && (
                        <tr>
                          <td className="text-left">{getText('main:priceinfo.CHARGING_TIME')}</td>
                          <td className="wd-100px text-right">{quickshop.evChargingBasicFees?.feeChargingPerMinute.toFixed(2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {groupedKeys.length > 0 &&
                    groupedKeys.map((key) => {
                      let categoryLabel = '';
                      switch (key) {
                        case LINE_ITEM_CATEGORY_GENERAL:
                          categoryLabel = getText('main:priceinfo.GENERAL');
                          break;
                        case LINE_ITEM_CATEGORY_RESTRICTIONS:
                          categoryLabel = getText('main:priceinfo.RESTRICTIONS');
                          break;
                        case LINE_ITEM_CATEGORY_PAYMENT_METHODS:
                          categoryLabel = getText('main:priceinfo.PAYMENT_METHODS');
                          break;
                      }
                      return (
                        <Table responsive className="bg-white template-tbl">
                          <thead>
                            <tr>
                              <td className="text-bold text-left">{categoryLabel}</td>
                              <td className="wd-100px text-bold text-right">
                                {getText('main:priceinfo.PRICE')}&nbsp;({quickshop.currency})
                              </td>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedPublicProductInformation[key].map((info) => {
                              return (
                                <tr>
                                  <td className="text-left">{info.lineItemText}</td>
                                  <td className="wd-100px text-right">{info.pricePerUnitInclVat && info.pricePerUnitInclVat?.toFixed?.(2) ? (info.pricePerUnitInclVat > 0 ? info.pricePerUnitInclVat?.toFixed?.(2) : '') : ''}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      );
                    })}
                </Collapsible>
              )}
              <div className="price-section">
                <h6 className="text-center">
                  <b>{getText('main:RESERVATION_AMOUNT')}:</b>
                  <br />
                  <p className="lbl-price">{`${quickshop.preAuthorizationAmount || 0}${quickshop.currency?.length > 0 ? ` ${quickshop.currency}` : ''}`}</p>
                </h6>
              </div>
              {!isPaymentInProgress && (
                <div className="text-center mb-20 box-charging">
                  {quickshop.isAutopilotEnabled === true && quickshop.statusCode === STATUS_CODE_CHARGING && quickshop.paymentStatusCode !== STATUS_WAITING_FOR_PAYMENT && quickshop.paymentStatusCode !== STATUS_PAID && (
                    <Button className="btn-start btn-charging" onClick={() => onStartCharging(quickshop._id)}>
                      {getText('main:START_CHARGING')}
                    </Button>
                  )}
                  {quickshop.isAutopilotEnabled === true && quickshop.statusCode !== STATUS_CODE_CHARGING && (
                    <div>
                      <p className="connect-car-txt">{getText('main:CONNECT_CAR_TO_CHARGER_MSG')}</p>
                      <p>
                        {getText('main:PAGE_RELOAD_TEXT')} {timeLeft} {getText('main:SECONDS')}.
                      </p>
                    </div>
                  )}
                  {quickshop.isAutopilotEnabled === true && quickshop.statusCode === STATUS_CODE_PREPARING && <p className="check-connection-txt blink">{getText('main:CHECKING_CONNECTION_MSG')}</p>}
                  {(!quickshop?.isAutopilotEnabled || quickshop?.isAutopilotEnabled === false) && quickshop.paymentStatusCode !== STATUS_WAITING_FOR_PAYMENT && quickshop.paymentStatusCode !== STATUS_PAID && (quickshop?.statusCode === STATUS_CODE_AVAILABLE || quickshop?.statusCode === STATUS_CODE_PREPARING) && (
                    <Button className="btn-start btn-charging" onClick={() => onStartCharging(quickshop._id)}>
                      {getText('main:START_CHARGING')}
                    </Button>
                  )}
                  <br />
                  {(quickshop?.statusCode === STATUS_CODE_CHARGING || quickshop?.statusCode === STATUS_CODE_SUSPENDEDEVSE || quickshop?.statusCode === STATUS_CODE_SUSPENDEDEVSE || quickshop?.statusCode === STATUS_CODE_FINISHING || quickshop?.statusCode === STATUS_CODE_RESERVED) && (
                    <Button className="btn-stop btn-charging" onClick={() => onStopCharging(quickshop._id)}>
                      {getText('main:STOP_CHARGING')}
                    </Button>
                  )}
                  <br />
                  {quickshop?.statusCode === STATUS_CODE_CHARGING && quickshop?.paymentStatusCode === STATUS_WAITING_FOR_PAYMENT && (
                    <Button className="btn pay-now-btn mt-10 pay-now-submit-btn ml-2 pb-2 pt-2" onClick={() => onPayAlreadyStartedPOSSession(quickshop._id)}>
                      {getText('main:PAY_NOW')}
                    </Button>
                  )}
                  {isQuickshopRentalSet && <div>{quickshop.rental.customerName}</div>}
                </div>
              )}
              {isPaymentInProgress && (
                <div className="payment-main-section">
                  <h5 className="border-bottom border-gray pb-2 mb-0">{getText('main:PAYMENT')}</h5>
                  <p className="text-left mt-10">{getText('main:PAYMENT_TEXT').replace('<amount>', `${quickshop.preAuthorizationAmount || 0}${quickshop.currency?.length > 0 ? ` ${quickshop.currency}` : ''}`)}</p>
                  {quickshop.statusCode !== STATUS_CODE_CHARGING && quickshop.statusCode !== STATUS_CODE_OUT_OF_SERVICE && (
                    <div className="form-horizontal receipt-box">
                      <label className="c-checkbox display-block mb-0">
                        <Input id="chkSendReceipt" checked={sendReceipt} name="sendReceipt" value={sendReceipt} onChange={() => setSendReceipt(!sendReceipt)} type="checkbox" />
                        <span className="fa fa-check"></span>
                        <b>{getText('main:EMAIL_TEXT')}</b>
                      </label>
                      {sendReceipt && [
                        <FormGroup key="1" className="text-left email-section">
                          <label>{getText('main:EMAIL')}:</label>
                          <Input type="text" name="email" value={email || ''} onChange={(ev) => setEmail(ev.target.value)} />
                        </FormGroup>,
                        <FormGroup key="2" className="text-left email-section">
                          <label>{getText('main:PHONE')}:</label>
                          <ReactPhoneInput
                            onBlur={(ev, country) => {
                              const phone = ev.target.value;
                              const value = phone.replace(`+${country.dialCode}`, '');
                              if (value.substring(0, 1) === '0') {
                                setPhone(`+${country.dialCode}${value.substring(1, value.length)}`);
                                setPayMobilePhone(`+${country.dialCode}${value.substring(1, value.length)}`);
                              }
                            }}
                            onlyCountries={COUNTRIES_EXTN.map((country) => country.code)}
                            value={phone.length === 0 ? '+46' : phone}
                            countryCodeEditable={false}
                            autoFormat={false}
                            className="m-0 p-0"
                            inputExtraProps={{ name: 'phone' }}
                            preferredCountries={PREFERRED_COUNTRIES}
                            onChange={(val) => {
                              setPhone(val);
                              setPayMobilePhone(val);
                            }}
                          />
                        </FormGroup>,
                      ]}
                    </div>
                  )}

                  <div className="media text-muted payment-section mt-0 pt-10 pl-0">
                    <div className="media-body mb-0 lh-125">
                      <div className="d-flex flex-column justify-content-between align-items-between w-100">
                        <p className="text-left mb-0 color-black">
                          <b>{getText('main:PAYMENT_TYPES_TEXT')}</b>
                        </p>
                        <div className="d-flex justify-content-between flex-column">
                          {quickshop.paymentProviderAccounts &&
                            Object.keys(quickshop.paymentProviderAccounts).map((pspType, idx) => {
                              const account = quickshop.paymentProviderAccounts[pspType];
                              const lang = language?.length > 0 && filter(LANGUAGES, { code: language }).length > 0 ? filter(LANGUAGES, { code: language })[0]?.value : filter(LANGUAGES, { code: ENGLISH_LANG })[0].value;
                              let text = quickshop?.paymentProviderAccounts?.[pspType]?.publicPSPName?.[lang]?.text || quickshop.paymentProviderAccounts[pspType].paymentProviderAccountName;
                              let imageElement = <img src={account.pspLogoImageURL} className="prod-img pull-right" />;

                              if (pspType === PSP_TYPE_STRIPE) {
                                text = PSP_TYPE_TEXT_CREDIT_CARD;
                                imageElement = (
                                  <div className="d-flex align-items-center justify-content-end prod-img pull-right">
                                    <FontAwesomeIcon icon={faCreditCard} size="2x" className="mr-2 text-primary" />
                                    <FontAwesomeIcon icon={faWallet} size="2x" className="text-primary" />
                                  </div>
                                );
                              } else if (pspType === PSP_TYPE_SWISH) {
                                text = PSP_TYPE_TEXT_SWISH;
                              }
                              return (
                                <FormGroup
                                  className="text-left mb-1 payment-type"
                                  onClick={() => {
                                    setSelectedPSPType(account.pspType);
                                    if (pspType === PSP_TYPE_STRIPE && !pspClientSecret) {
                                      onPay(account.pspType);
                                    }
                                  }}
                                  key={idx}
                                >
                                  <Label check className="ml-20 pt-12">
                                    <Input type="radio" name="radio1" checked={selectedPSPType === account.pspType} onChange={() => setSelectedPSPType(account.pspType)} />
                                    {text}
                                  </Label>
                                  {imageElement}
                                </FormGroup>
                              );
                            })}
                          {selectedPSPType === PSP_TYPE_SWISH && (
                            <div className="form-group left-receipt-box mb-10" id="phoneText">
                              <FormGroup className="text-left email-section">
                                <label className="mb-0 mt-1">{getText('main:PHONE')}</label>
                                <div className="info-tooltip mt-1">
                                  <em className="fa fa-info-circle" data-tip data-for="mobilePhone"></em>
                                  <ReactTooltip id="mobilePhone" effect="solid" className="customTooltip">
                                    <span>{getText('main:ENTER_MOBILE_NUMBER')}</span>
                                  </ReactTooltip>
                                </div>
                                <ReactPhoneInput
                                  onBlur={(ev, country) => {
                                    const phone = ev.target.value;
                                    const value = phone.replace(`+${country.dialCode}`, '');
                                    if (value.substring(0, 1) === '0') {
                                      setPayMobilePhone(`+${country.dialCode}${value.substring(1, value.length)}`);
                                    }
                                  }}
                                  onlyCountries={COUNTRIES_EXTN.map((country) => country.code)}
                                  value={payMobilePhone.length === 0 ? '+46' : payMobilePhone}
                                  countryCodeEditable={false}
                                  autoFormat={false}
                                  className="m-0 p-0"
                                  inputExtraProps={{ name: 'phone' }}
                                  preferredCountries={PREFERRED_COUNTRIES}
                                  onChange={(val) => setPayMobilePhone(val)}
                                />
                              </FormGroup>
                            </div>
                          )}
                        </div>
                        {isNoPSPTypeSelected && (
                          <div className="form-group" id="phoneMessageBox">
                            <label className="validation-msg">
                              <i className="fa fa-info-circle validation-msg-inner mr-1"></i>
                              <span id="phoneMessage" className="validation-msg-inner">
                                {getText('main:NO_PSP_TYPE_SELECTED_MSG')}
                              </span>
                            </label>
                          </div>
                        )}
                        {isPhoneValidationFailed && (
                          <div className="form-group" id="phoneMessageBox">
                            <label className="validation-msg">
                              <i className="fa fa-info-circle validation-msg-inner mr-1"></i>
                              <span id="phoneMessage" className="validation-msg-inner">
                                {getText('main:PHONE_VALIDATION_FAILED_MSG')}
                              </span>
                            </label>
                          </div>
                        )}
                        <div className="pb-3 chkbox-text" style={{ textAlign: 'left' }}>
                          {quickshop.privacyPolicyURL && (
                            <span>
                              {getText('main:PAYMENT_TEXT_LINE1')}{' '}
                              <a href={quickshop.privacyPolicyURL} target="_blank" className="text-link">
                                {getText('main:PAYMENT_LINK_LINE1')}
                              </a>
                            </span>
                          )}{' '}
                          {quickshop.customerAgreementURL && (
                            <span>
                              {getText('main:PAYMENT_TEXT_LINE2')}{' '}
                              <a href={quickshop.customerAgreementURL} target="_blank" className="text-link">
                                {getText('main:PAYMENT_LINK_LINE2')}
                              </a>
                            </span>
                          )}
                        </div>
                      </div>
                      {pspClientSecret?.length > 0 && selectedPSPType === PSP_TYPE_STRIPE ? (
                        <Elements stripe={stripePromise} options={options}>
                          <CheckoutForm id={quickshop._id} setSelectedPSPType={(val) => setSelectedPSPType(val)} setIsPaymentCanceled={() => setIsPaymentCanceled()} />
                        </Elements>
                      ) : (
                        <div className="text-center box-charging">
                          <button type="button" onClick={() => onCancel()} className="btn pay-now-cancel-btn">
                            {getText('main:CANCEL')}
                          </button>
                          <button type="button" onClick={() => onPay(selectedPSPType)} className="btn pay-now-btn btn-success ml-2">
                            {getText('main:CONTINUE')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {quickshop.statusCode !== STATUS_CODE_CHARGING && quickshop?.moreInfoURL?.length > 0 && (
                <div className="how-to-link text-center">
                  <a href={quickshop.moreInfoURL} target="_blank" className="table-pointer">
                    {getText('main:HOW_TO_LINK_TEXT')}
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="inactive-quickshop">{getText('main:QUICKSHOP_NOT_ACTIVE_MSG')}</div>
        )}
        <div className="col"></div>
      </div>
    </div>
  );
};

EVChargingQuickshop.displayName = 'EVChargingQuickshop';

EVChargingQuickshop.propTypes = {};

export default EVChargingQuickshop;
